import { List } from '@/composables/GraphQL';
import {
  CampaignsBulk,
  CampaignsAutomation,
  OperatorType,
} from '@/types/generated-types/graphql';
import { CampaignsBulkStatusEnum, IntervalDateEnum, CampaignAutomationStatusEnum } from '@/types';
import { UserState } from '@/composables/User';
import { fetchDataSourcesWithInactive } from '@/composables/segmentations/helpers/fetchData';
import { DataSourceOption } from '@/composables/data-sources/dataSourceTree';
import moment, { DurationInputArg1 } from 'moment/moment';
import DurationConstructor = moment.unitOfTime.DurationConstructor;

export interface StatsPageFiltersMetaData {
  date_interval: {
    label: string;
    component: string;
  };
  id_campaign_automation: {
    label: string;
    component: string;
    options: () => Promise<{ name: string; id: number }[]>;
  };
  id_campaign_bulk: {
    label: string;
    component: string;
    options: () => Promise<{ name: string; id: number }[]>;
  };
  id_data_source: {
    label: string;
    component: string;
    optionKey: string;
    multiSourceOnly: boolean;
    options: () => Promise<DataSourceOption[]>;
  };
}

export const StatsPageFiltersMetaData: any = {
  date_interval: {
    label: 'statsEditor.sidebarPanel.filters.dateInterval',
    component: 'CustomDatePicker',
  },
  id_campaign_automation: {
    label: 'statsEditor.sidebarPanel.filters.automationCampaigns',
    component: 'MultiSelect',
    defaultData: {
      visibleOnView: true,
      value: [],
    },
    getOptions: async () => {
      let result: any = [];
      const idShop = UserState.activeShop?.id ?? 0;

      const data = await List<CampaignsAutomation>({
        name: 'CampaignsAutomation',
        settings: {
          limit: 0,
          offset: 0,
          order: [
            {
              field: 'date_modification',
              type: 'ASC',
            },
          ],
          filter: [
            { field: 'status', value: CampaignAutomationStatusEnum.ARCHIVED, operator: OperatorType.NotEquals },
            { field: 'status', value: CampaignAutomationStatusEnum.DELETED, operator: OperatorType.NotEquals },
            { field: 'id_shop', value: idShop, operator: OperatorType.Equals },
          ],
        },
        fields: ['id_campaign_automation', 'name'],
      });

      if (data && data.items && data.items.length) {
        result = data.items.map((item) => ({
          name: item.name,
          id: item.id_campaign_automation,
        }));
      }

      return result;
    },
  },
  id_campaign_bulk: {
    label: 'statsEditor.sidebarPanel.filters.bulkCampaigns',
    component: 'Dropdown',
    defaultData: {
      visibleOnView: true,
      value: [],
    },
    getOptions: async (currentFilters: any) => {
      // We calculate the minimum date according to current filter
      let minimumDate;
      const dateInterval = currentFilters.date_interval.value.interval;
      if (dateInterval === IntervalDateEnum.CUSTOM_DATE_RANGE) {
        // Custom range
        const { customDateRange } = currentFilters.date_interval.value;
        minimumDate = moment(customDateRange[0]);
      } else {
        // Interval from now
        let intervalValue: DurationInputArg1;
        let intervalUnit: DurationConstructor;

        switch (dateInterval) {
          case IntervalDateEnum.LAST_DAY:
            intervalValue = 1;
            intervalUnit = 'days';
            break;
          case IntervalDateEnum.LAST_3_DAYS:
            intervalValue = 3;
            intervalUnit = 'days';
            break;
          case IntervalDateEnum.LAST_7_DAYS:
            intervalValue = 7;
            intervalUnit = 'days';
            break;
          case IntervalDateEnum.LAST_14_DAYS:
            intervalValue = 14;
            intervalUnit = 'days';
            break;
          case IntervalDateEnum.LAST_30_DAYS:
            intervalValue = 30;
            intervalUnit = 'days';
            break;
          case IntervalDateEnum.LAST_3_MONTHS:
            intervalValue = 3;
            intervalUnit = 'months';
            break;
          case IntervalDateEnum.LAST_12_MONTHS:
            intervalValue = 12;
            intervalUnit = 'months';
            break;
          default:
            intervalValue = 30;
            intervalUnit = 'days';
            break;
        }

        minimumDate = moment().subtract(intervalValue, intervalUnit);
      }

      let result: any = [];
      const idShop = UserState.activeShop?.id ?? 0;

      const data = await List<CampaignsBulk>({
        name: 'CampaignsBulk',
        settings: {
          limit: 0,
          offset: 0,
          order: [
            {
              field: 'date_send',
              type: 'DESC',
            },
          ],
          filter: [
            { field: 'id_shop', value: idShop, operator: OperatorType.Equals },
            {
              field: 'status',
              value: [
                CampaignsBulkStatusEnum.SENDING,
                CampaignsBulkStatusEnum.SENT,
                CampaignsBulkStatusEnum.SUSPENDED,
                CampaignsBulkStatusEnum.SUSPENDED_BY_USER,
              ].join(','),
              operator: OperatorType.In,
            },
            {
              field: 'date_send',
              value: minimumDate.format('YYYY-MM-DD 00:00:00'),
              operator: OperatorType.GreaterThanOrEqualTo,
            },
          ],
        },
        fields: ['id_campaign_bulk', 'name', 'date_send'],
      });

      if (data && data.items && data.items.length) {
        result = data.items.map((item) => ({
          name: item.name,
          id: item.id_campaign_bulk,
          dateSend: item.date_send,
        }));
      }

      return result;
    },
  },
  // Se règle comme le filtre « Automatisation », mais se présente comme le
  // sélecteur du header (arbre, cascade). Valeur vide = tous les points de
  // vente. Les points de vente désactivés restent proposés : l'historique d'un
  // magasin fermé compte toujours. Seuls les widgets qui déclarent ce filtre
  // (ex. « Chiffre d'affaires par source ») en tiennent compte. Réservé aux
  // boutiques multi-sources, comme le sélecteur du header.
  id_data_source: {
    label: 'statsEditor.sidebarPanel.filters.dataSources',
    component: 'DataSourceFilterSelect',
    optionKey: 'value',
    multiSourceOnly: true,
    defaultData: {
      visibleOnView: true,
      value: [],
    },
    getOptions: async (): Promise<DataSourceOption[]> => fetchDataSourcesWithInactive(),
  },
};

/**
 * Champ qui identifie une option dans les listes de choix des filtres.
 *
 * PrimeVue s'en sert comme clé de rendu ; sans lui, il retombe sur le libellé.
 * Deux campagnes peuvent porter le même nom, et Vue ne sait alors plus
 * distinguer leurs deux lignes : en cours de frappe dans la recherche, des
 * lignes qui ne correspondent plus restent affichées et ne se cochent pas.
 * L'identifiant, lui, est unique.
 */
export const FILTER_OPTION_KEY = 'id';

/**
 * Clé de rendu à donner au contrôle d'un filtre, ou `undefined` pour ceux qui
 * n'ont pas de liste d'options — un sélecteur de dates, par exemple. Un filtre
 * dont les options nomment autrement leur identifiant le déclare (`optionKey`).
 */
export function getFilterOptionKey(filterId: string): string | undefined {
  return StatsPageFiltersMetaData[filterId]?.getOptions
    ? StatsPageFiltersMetaData[filterId].optionKey ?? FILTER_OPTION_KEY
    : undefined;
}

/**
 * Un filtre réservé aux boutiques multi-sources (`multiSourceOnly`) n'est ni
 * proposé ni affiché ailleurs ; `multiSource` vient de `isMultiSourceShop`.
 */
export function isPageFilterAvailable(filterId: string, multiSource: boolean): boolean {
  return multiSource || !StatsPageFiltersMetaData[filterId]?.multiSourceOnly;
}
